/* ─── Serpent & the Swan — Scroll Animations ───────────────────────────────
   GSAP + ScrollTrigger (vendored in assets/).
   Uses ScrollTrigger.batch() for coordinated stagger reveals.
   Uses autoAlpha (opacity + visibility) so invisible elements don't block clicks.
   Respects prefers-reduced-motion throughout.
   ─────────────────────────────────────────────────────────────────────────── */

(function () {
  // Wait for GSAP — both scripts are defer-loaded so execution order is
  // guaranteed, but cap retries in case either file fails to load.
  let _retries = 0;
  function init() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      if (++_retries > 40) return; // bail after ~2 seconds
      setTimeout(init, 50);
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    // Bail out entirely for users who prefer reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    /* ── Defaults ─────────────────────────────────────────────────────────── */
    const EASE     = 'power2.out';
    const DURATION = 0.7;

    /* ── Pre-detect explosion cells ────────────────────────────────────────── *
       Cells that will be used in the pinned explosion sequence must be excluded
       from the generic step-4 stagger batch — otherwise the batch's
       overwrite:true tween kills the scrub timeline tweens on the same elements.
    ─────────────────────────────────────────────────────────────────────────── */
    const explosionCells = new Set();
    (function detectExplosionCells() {
      const stage = document.querySelector('.section-solid-gold-collection');
      if (!stage) return;
      // Explosion runs when the reveal overlay is present (reveal_heading set in section settings).
      if (!stage.querySelector('.solid-gold-reveal-overlay')) return;
      stage.querySelectorAll('.solid-gold-collage__item').forEach((c) => explosionCells.add(c));
    })();

    /* ── 1. Section reveals — ScrollTrigger.batch() ───────────────────────── *
       Batch coordinates all sections entering the viewport around the same
       time into a single stagger callback — far fewer active ScrollTriggers
       than one-per-element. Skip index 0 (above-fold hero) and the solid gold
       section (its cells have their own stagger; competing tweens look wrong).
    ─────────────────────────────────────────────────────────────────────────── */
    const sectionTargets = Array.from(
      document.querySelectorAll('.section-container')
    ).filter((el, i) =>
      i !== 0
      && !el.querySelector('.solid-gold-collage')
      && !el.classList.contains('section-handcrafted-collage')
    );

    if (sectionTargets.length) {
      // Set initial hidden state (autoAlpha handles opacity + visibility)
      gsap.set(sectionTargets, { autoAlpha: 0, y: 32 });

      ScrollTrigger.batch(sectionTargets, {
        start: 'top 88%',
        once: true,
        onEnter: (batch) =>
          gsap.to(batch, {
            autoAlpha: 1,
            y: 0,
            duration: DURATION,
            ease: EASE,
            stagger: 0.06,
          }),
      });
    }

    /* ── 2. Section h2 heading reveals — batch ────────────────────────────── *
       Headings animate slightly ahead of their section's body content,
       giving a "headline drops in, content follows" editorial feel.
    ─────────────────────────────────────────────────────────────────────────── */
    const headings = document.querySelectorAll('.section-container h2');
    if (headings.length) {
      gsap.set(headings, { autoAlpha: 0, y: 20 });
      ScrollTrigger.batch(headings, {
        start: 'top 90%',
        once: true,
        onEnter: (batch) =>
          gsap.to(batch, {
            autoAlpha: 1,
            y: 0,
            duration: 0.6,
            ease: 'power3.out',
            stagger: 0.05,
          }),
      });
    }

    /* ── 3. Product grid stagger — batch ─────────────────────────────────── *
       Cards in collection/search grids cascade in left-to-right.
       batchMax matches the typical grid column count so each row reveals
       together rather than as one enormous single stagger.
    ─────────────────────────────────────────────────────────────────────────── */
    document.querySelectorAll('.collection-grid, .product-grid').forEach((grid) => {
      const cards = grid.querySelectorAll('.card-product-wrapper, .card-wrapper, li');
      if (!cards.length) return;

      gsap.set(cards, { autoAlpha: 0, y: 24 });

      ScrollTrigger.batch(cards, {
        start: 'top 85%',
        once: true,
        batchMax: 4, // animate one row at a time
        interval: 0.1,
        onEnter: (batch) =>
          gsap.to(batch, {
            autoAlpha: 1,
            y: 0,
            duration: 0.55,
            ease: EASE,
            stagger: 0.07,
            overwrite: true,
          }),
      });
    });

    /* ── 4. Solid Gold Collection collage stagger ────────────────────────── *
       Cells fan in with scale + fade. The parent section is excluded from
       the generic section reveal (step 1) to avoid competing tweens.
       Explosion cells (detected above) are skipped — the explosion handles them.
    ─────────────────────────────────────────────────────────────────────────── */
    document.querySelectorAll('.solid-gold-collage').forEach((collage) => {
      const cells = Array.from(collage.querySelectorAll('.solid-gold-collage__item'))
        .filter((c) => !explosionCells.has(c));
      if (!cells.length) return;

      gsap.set(cells, { autoAlpha: 0, scale: 0.97 });

      ScrollTrigger.batch(cells, {
        start: 'top 85%',
        once: true,
        onEnter: (batch) =>
          gsap.to(batch, {
            autoAlpha: 1,
            scale: 1,
            duration: 0.65,
            ease: EASE,
            stagger: 0.06,
            overwrite: true,
          }),
      });
    });

    /* ── 5. Image-with-text panels slide in from opposing sides ──────────── */
    const iwtMedia = document.querySelectorAll('.image-with-text__media');
    if (iwtMedia.length) {
      gsap.set(iwtMedia, { autoAlpha: 0, x: -24 });
      ScrollTrigger.batch(iwtMedia, {
        start: 'top 85%',
        once: true,
        onEnter: (batch) =>
          gsap.to(batch, { autoAlpha: 1, x: 0, duration: 0.75, ease: EASE }),
      });
    }

    const iwtContent = document.querySelectorAll('.image-with-text__content');
    if (iwtContent.length) {
      gsap.set(iwtContent, { autoAlpha: 0, x: 24 });
      ScrollTrigger.batch(iwtContent, {
        start: 'top 85%',
        once: true,
        onEnter: (batch) =>
          gsap.to(batch, { autoAlpha: 1, x: 0, duration: 0.75, ease: EASE }),
      });
    }

    /* ── 6. Section label / overline reveals ─────────────────────────────── */
    const labels = document.querySelectorAll('.section-label');
    if (labels.length) {
      gsap.set(labels, { autoAlpha: 0, y: 12 });
      ScrollTrigger.batch(labels, {
        start: 'top 88%',
        once: true,
        onEnter: (batch) =>
          gsap.to(batch, { autoAlpha: 1, y: 0, duration: 0.5, ease: EASE, stagger: 0.05 }),
      });
    }

    /* ── 7. Trust / feature icon rows ────────────────────────────────────── */
    document.querySelectorAll('.footer-features, .trust-badges').forEach((row) => {
      const items = Array.from(row.querySelectorAll(':scope > *'));
      if (!items.length) return;
      gsap.set(items, { autoAlpha: 0, y: 16 });
      ScrollTrigger.batch(items, {
        start: 'top 90%',
        once: true,
        onEnter: (batch) =>
          gsap.to(batch, { autoAlpha: 1, y: 0, duration: 0.5, ease: EASE, stagger: 0.1 }),
      });
    });

    /* ── 8. Solid Gold explosion — pinned scroll reveal ─────────────────────── *
       Cells fly outward radially from grid centre as the user scrolls,
       revealing the "Forever Jewellery" rich-text section underneath.
       The rich-text content is cloned into an overlay inside the solid gold
       section so it appears in-place as cells clear. The original rich-text
       section is temporarily hidden to avoid duplication.
    ─────────────────────────────────────────────────────────────────────────── */
    initGoldExplosion();

    /* ── 9. Handcrafted Collage — pinned, scrubbed image + message reveal ───── *
       Images fade in staggered over the first 20% of scroll, hold through the
       middle, and fade out over the final 20%. Two editorial messages
       cross-fade: message 1 visible 20–60%, message 2 visible 60–100%.
    ─────────────────────────────────────────────────────────────────────────── */
    initHandcraftedCollage();

    /* ── Shopify theme editor: refresh triggers on section change ─────────── *
       ScrollTrigger.refresh() is auto-debounced on viewport resize.
       Only call explicitly for Shopify's discrete section events.
    ─────────────────────────────────────────────────────────────────────────── */
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.addEventListener('shopify:section:load', () => ScrollTrigger.refresh());
      document.addEventListener('shopify:section:reorder', () => ScrollTrigger.refresh());
    }
  }

  /* ── Gold Explosion — standalone function ────────────────────────────────── */
  function initGoldExplosion() {
    const stage = document.querySelector('.section-solid-gold-collection');
    if (!stage) return;

    const cells = stage.querySelectorAll('.solid-gold-collage__item');
    if (!cells.length) return;

    // Overlay is server-rendered in the section template — no cloning needed.
    const overlay = stage.querySelector('.solid-gold-reveal-overlay');

    // Bail if there's no reveal text configured (overlay won't be in DOM).
    if (!overlay) return;

    // Move overlay to <body> so its position:fixed is always relative to the
    // viewport — not to any transformed ancestor created by GSAP's pin.
    document.body.appendChild(overlay);

    // Ensure explosion cells are fully visible at pin start — step 4 was
    // skipped for these cells so no stagger batch is competing with us.
    gsap.set(cells, { autoAlpha: 1, scale: 1 });

    // ── Calculate explosion vectors — must happen before pin (layout changes) ──
    const collage = stage.querySelector('.solid-gold-collage');
    if (!collage) return;
    const cr = collage.getBoundingClientRect();
    const cx = cr.left + cr.width / 2;
    const cy = cr.top + cr.height / 2;

    const isMobile = window.innerWidth < 750;
    const THROW = isMobile ? 120 : 220;

    const cellData = Array.from(cells).map((cell) => {
      const r = cell.getBoundingClientRect();
      const dx = r.left + r.width / 2 - cx;
      const dy = r.top + r.height / 2 - cy;
      const mag = Math.sqrt(dx * dx + dy * dy) || 1;
      return { cell, nx: dx / mag, ny: dy / mag };
    });

    // ── Build the scrubbed timeline ───────────────────────────────────────────
    // start: 'bottom bottom' — the section scrolls naturally into full view
    // (bottom of grid hits bottom of viewport) before pinning. The explosion
    // then plays as the user continues scrolling.
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: stage,
        start: 'bottom bottom',
        end: isMobile ? '+=80%' : '+=130%',
        pin: true,
        scrub: 1.2,
        anticipatePin: 1,
      },
    });

    // Phase 1 (staggered): each cell flies radially outward + fades
    cellData.forEach(({ cell, nx, ny }, i) => {
      tl.to(
        cell,
        { x: nx * THROW, y: ny * THROW, autoAlpha: 0, duration: 0.65, ease: 'power2.in' },
        i * 0.05
      );
    });

    // Phase 2: overlay fades in as cells clear
    tl.to(overlay, { autoAlpha: 1, duration: 0.5, ease: 'power2.out' }, 0.4);

    // Phase 3: overlay fades back out at the very end of the timeline so that
    // progress=1 (scrub complete) means overlay is hidden — the scrub lag tween
    // would otherwise re-drive the timeline to progress=1 and override any
    // callback-based set, leaving the overlay stuck on screen.
    tl.to(overlay, { autoAlpha: 0, duration: 0.4, ease: 'power2.in' }, '>+0.3');
  }

  /* ── Handcrafted Collage — standalone function ───────────────────────────── *
     Sequential FIFO wave: images fade in one at a time in block order, hold
     briefly, then fade out in the same order (first-in, first-out). Message 1
     comes in once roughly half the images are visible. Message 2 comes in as
     the images start fading out. Continuous motion across the entire scrub.
  ─────────────────────────────────────────────────────────────────────────── */
  function initHandcraftedCollage() {
    document.querySelectorAll('.section-handcrafted-collage').forEach((stage) => {
      const items = Array.from(stage.querySelectorAll('.handcrafted-collage__item'));
      const msg1  = stage.querySelector('.handcrafted-collage__message--1');
      const msg2  = stage.querySelector('.handcrafted-collage__message--2');
      if (!items.length && !msg1 && !msg2) return;

      if (items.length) gsap.set(items, { autoAlpha: 0, scale: 0.96 });
      if (msg1) gsap.set(msg1, { autoAlpha: 0 });
      if (msg2) gsap.set(msg2, { autoAlpha: 0 });

      const isMobile = window.innerWidth < 750;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: stage,
          start: 'top top',
          end: isMobile ? '+=180%' : '+=240%',
          pin: true,
          scrub: 1.2,
          anticipatePin: 1,
        },
      });

      const N = items.length;
      const FADE_IN_END   = 0.40; // all images visible by here
      const FADE_OUT_START = 0.55; // first image starts disappearing
      const FADE_OUT_END   = 0.93; // all gone by here

      // Phase 1: sequential fade-in across 0 → FADE_IN_END.
      items.forEach((item, i) => {
        const at = N > 1 ? (i / (N - 1)) * FADE_IN_END : 0;
        tl.to(item, { autoAlpha: 1, scale: 1, duration: 0.14, ease: 'power2.out' }, at);
      });

      // Message 1: in once roughly half the images have appeared,
      // out just before the fade-out wave begins.
      if (msg1) {
        const halfIdx = Math.floor((N - 1) / 2);
        const halfAt  = N > 1 ? (halfIdx / (N - 1)) * FADE_IN_END : 0;
        tl.to(msg1, { autoAlpha: 1, duration: 0.14, ease: 'power2.out' }, halfAt + 0.05)
          .to(msg1, { autoAlpha: 0, duration: 0.10, ease: 'power2.in'  }, FADE_OUT_START - 0.02);
      }

      // Message 2: in as the first image starts to fade out, out at the end.
      if (msg2) {
        tl.to(msg2, { autoAlpha: 1, duration: 0.14, ease: 'power2.out' }, FADE_OUT_START)
          .to(msg2, { autoAlpha: 0, duration: 0.10, ease: 'power2.in'  }, FADE_OUT_END);
      }

      // Phase 2: sequential fade-out (same FIFO order) across FADE_OUT_START → FADE_OUT_END.
      items.forEach((item, i) => {
        const at = N > 1
          ? FADE_OUT_START + (i / (N - 1)) * (FADE_OUT_END - FADE_OUT_START)
          : FADE_OUT_START;
        tl.to(item, { autoAlpha: 0, duration: 0.12, ease: 'power2.in' }, at);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
